import { useAxiosStore } from '@risksmart-app/components/src/hooks/useAxios';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { getEnv } from '@risksmart-app/components/src/utils/environment';

import { useConnectSlackTRPC } from '@/hooks/mutations/notifications/useConnectSlackTRPC';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { handleError } from '@/utils/errorUtils';

const scopes = ['chat:write'];
const redirectUri =
  window.location.hostname === 'localhost'
    ? 'https://redirectmeto.com/http://localhost:3000/slack-callback' // hacky workaround for slack redirect on localhost
    : window.location.origin + '/slack-callback';
const NONCE_STORAGE_KEY = 'slack-oauth-nonce';

// Generate a random nonce value for the OAuth state parameter.
function generateNonce() {
  const charset =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._~';
  const result: string[] = [];
  window.crypto
    .getRandomValues(new Uint8Array(32))
    .forEach((c) => result.push(charset[c % charset.length]));

  return result.join('');
}

/*
 * useSlack hook provides functions to connect/disconnect Slack from Knock.
 */
export const useSlack = () => {
  const { authorisedAxiosInstance } = useAxiosStore();
  const { addNotification } = useNotifications();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const { connectSlack } = useConnectSlackTRPC();

  /*
   * This function opens a new window to Slack's OAuth flow.
   */
  const loginWithSlack = () => {
    // Generate a random nonce value for the OAuth state parameter.
    const state = generateNonce();
    const slackClientId: string | undefined = getEnv(
      'REACT_APP_SLACK_CLIENT_ID',
      true
    );

    if (!slackClientId) {
      addNotification({
        content: 'Failed to launch slack login. Client ID not set',
        type: 'error',
      });

      return Promise.reject();
    }

    sessionStorage.setItem(NONCE_STORAGE_KEY, state);

    const openedWindow = window.open(
      `https://slack.com/oauth/v2/authorize?scope=${scopes.join(
        ','
      )}&client_id=${slackClientId}&redirect_uri=${redirectUri}&state=${state}`
    );

    return new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (openedWindow?.closed) {
          clearInterval(interval);
          resolve();
        }
      }, 500);
    });
  };

  /*
   * This function exchanges the code provided by Slack and connects Slack to Knock.
   *
   * The OAuth nonce is verified HERE, in the browser, on both paths — neither
   * the legacy `POST /slack/callback` rest-api handler nor its V3 replacement
   * (`frontend.notifications.slack.connect`, COI-705) inspects `state`.
   * Mismatch throws before any request is made, as it always has.
   */
  const exchangeSlackCode = async (code: string, state: string) => {
    const storedState = sessionStorage.getItem(NONCE_STORAGE_KEY);
    sessionStorage.removeItem(NONCE_STORAGE_KEY);
    if (state !== storedState) {
      throw new Error('Invalid state');
    }

    try {
      if (trpcEnabled) {
        await connectSlack(code, state);
      } else {
        await authorisedAxiosInstance.post('/slack/callback', { code, state });
      }

      return true;
    } catch (error) {
      // Report before swallowing. The boolean contract is deliberate — the
      // callback page renders its own failure state and must not toast — but a
      // silently-dropped exchange failure is undiagnosable, and this is the only
      // catch in the flow.
      handleError(error);

      return false;
    }
  };

  return { loginWithSlack, exchangeSlackCode };
};
